import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { api } from '@/utils/api';

interface Notification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  is_read: number;
  created_at: string;
}

export function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchCount = useCallback(async () => {
    try {
      const data = await api.get<{ count: number }>('/notifications/count');
      setCount(data.count);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  const fetchNotifications = async () => {
    try {
      const data = await api.get<{ notifications: Notification[] }>('/notifications?limit=20');
      setNotifications(data.notifications);
    } catch { /* ignore */ }
  };

  const handleOpen = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const handleMarkRead = async (id: number) => {
    await api.patch(`/notifications/${id}/read`);
    setNotifications(n => n.map(item => item.id === id ? { ...item, is_read: 1 } : item));
    setCount(c => Math.max(0, c - 1));
  };

  const handleMarkAllRead = async () => {
    await api.patch('/notifications/read-all');
    setNotifications(n => n.map(item => ({ ...item, is_read: 1 })));
    setCount(0);
  };

  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ left: 0, bottom: 0 });

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ left: rect.left, bottom: window.innerHeight - rect.top + 8 });
    }
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="relative text-muted hover:text-default transition-colors"
        title="Benachrichtigungen"
      >
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-accent text-white text-[9px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-bold">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="fixed w-80 bg-elevated border border-default rounded-lg shadow-lg z-40 overflow-hidden" style={{ left: pos.left, bottom: pos.bottom }}>
            <div className="flex items-center justify-between p-3 border-b border-default">
              <span className="text-sm font-medium">Benachrichtigungen</span>
              {count > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-accent hover:underline flex items-center gap-1">
                  <CheckCheck className="w-3 h-3" /> Alle gelesen
                </button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-dim">Keine Benachrichtigungen</div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-2 px-3 py-2 border-b border-default/50 transition-colors ${
                      n.is_read ? 'opacity-60' : 'bg-hover/30'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-dim truncate">{n.body}</p>}
                      <p className="text-[10px] text-dim mt-0.5">
                        {new Date(n.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {!n.is_read && (
                      <button
                        onClick={() => handleMarkRead(n.id)}
                        className="text-dim hover:text-accent shrink-0 mt-0.5"
                        title="Als gelesen markieren"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
